// =============================================================================
// ui/tabs/JournalSection.tsx — the journal's `.jp-sec` card, shared by all three
// phases.
// -----------------------------------------------------------------------------
// ── 🚨 WHY THIS IS ITS OWN FILE ──
//
// There were TWO of these, and only one of them was right. `PlanEditor` had the
// real card — icon badge, title, optional meta, collapse chevron — while
// `Journal.tsx` kept a bare `<h4 class="jp-sec-title">` with no header row at
// all. So the Pre-Market phase got the fix from Josh's side-by-side and the
// Session and Recap phases, which nobody had compared, silently kept the old
// one. Same class name, different markup, on the same screen.
//
// That is the shared-name failure the relocation report warns about, one level
// up: the report compares CLASS names against the old client and had nothing to
// say about two COMPONENTS in this client disagreeing about the same class. A
// class used by more than one component belongs in one place.
//
// ── ⚠ COLLAPSE IS IN-SESSION ONLY, AND THAT MATCHES BETA ──
//
// The old client's `wireCollapse` says so in as many words: "Collapsible
// sections (ephemeral — not persisted in V1)". Open a section, reload, it is
// open again — in both clients. This was previously written up here as a gap;
// it is not one.
//
// What beta persists is a DIFFERENT thing: the `⊘ hide` buttons on three
// pre-market sections, which store hidden keys per user in settings and survive
// reload. That is a feature with its own storage, and it is genuinely absent
// here — see the "what's writable" card on the tab.
//
// ⚠ A BUTTON, not a div with a click handler, so the header is reachable by
// keyboard. Beta uses a div; this is the re-derived selector the relocation rule
// asks for — the values are beta's, the element is this client's, and the
// button reset in `.jp-sec-h` exists because of it.
// =============================================================================

import { useState, type ReactNode } from 'react';
import { useJournalHidden } from '../state/useJournalHidden';

export interface JournalSectionProps {
  /** Beta's glyph for this concept. Same character per section in both clients. */
  icon: string;
  title: string;
  /** The small right-hand note in beta's header — "live log", "no contract set". */
  meta?: string;
  /**
   * The key this section hides under, if it is hideable at all (TCR-81).
   *
   * ⚠ OMITTED MEANS NOT HIDEABLE, deliberately. A section with no key cannot
   * be hidden by anything, which is how the curated set stays curated — see
   * `domain/journalHidden.ts`.
   */
  hideId?: string;
  children: ReactNode;
}

export function JournalSection(
  { icon, title, meta, hideId, children }: JournalSectionProps,
) {
  const [collapsed, setCollapsed] = useState(false);
  /**
   * ⚠ HIDDEN AND COLLAPSED ARE DIFFERENT THINGS AND BOTH ARE CSS-ONLY.
   *
   * Collapsed is ephemeral and reopens on a header click. Hidden persists in
   * `app_flags` and only the gear brings it back (TCR-81).
   *
   * 🔴 NEITHER UNMOUNTS THE BODY, and since TCR-79 that is load-bearing rather
   * than merely tidy: both Journal editors now autosave on blur against a JSON
   * diff of their draft, so a hide that removed fields would make the next
   * autosave PERSIST the deletion as a real edit.
   */
  const { hidden } = useJournalHidden();
  const isHiddenHere = !!hideId && hidden.includes(hideId);

  return (
    <section
      className={`jp-sec${collapsed ? ' collapsed' : ''}${isHiddenHere ? ' jp-hidden' : ''}`}
      data-hide-id={hideId}
    >
      <button
        type="button"
        className="jp-sec-h"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
      >
        {/* ⚠ `aria-hidden` on the glyph. It is decoration; a screen reader
            announcing "black diamond" before every section title is noise. */}
        <span className="jp-sec-ic" aria-hidden="true">{icon}</span>
        <span className="jp-sec-title">{title}</span>
        {meta ? <span className="jp-sec-meta">{meta}</span> : null}
      </button>
      {/* ⚠ ALWAYS RENDERED. `.jp-sec.collapsed .jp-sec-body { display:none }` is
          what hides it, exactly as in beta — the inputs stay in the DOM so a
          collapsed section's draft is never discarded by collapsing it. */}
      <div className="jp-sec-body">{children}</div>
    </section>
  );
}
